import React from "react";

import { Activity, Download } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import { ButtonShadcn } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import { Section, KpiTile, EmptyNote, CHART, fmtDays, goodRateColor, ExportButtons } from "./primitives";

// F1 — process health & throughput. Data's job: a few headline magnitudes
// (→ KPI tiles) plus change-over-time (→ an area/line trend — the one form that
// reads a rate moving over weeks). This is the only time-series on the page, so
// the line chart is reserved for it and used nowhere else.

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border-primary bg-background-secondary px-2.5 py-1.5 shadow-lg">
      <p className="text-[11px] font-medium text-text-primary mb-0.5">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-[11px] text-text-secondary">
          <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold text-text-primary">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const OverviewSection = ({ data, loading, onExportCsv, onExportPdf, exporting }) => {
  const k = data?.kpis;
  const rateColor = goodRateColor(k?.complianceRate ?? 0);

  // Merge the two trends into one dataset for a single readable chart:
  // compliance rate (%) as the shaded area, sent volume as a companion line.
  const trend = (data?.rateTrend ?? []).map((pt, i) => ({
    label: pt.label,
    rate: pt.value,
    sent: data?.volumeTrend?.[i]?.value ?? pt.secondary ?? 0,
  }));

  return (
    <Section
      icon={Activity}
      title="Process health & throughput"
      subtitle="How is the whole compliance process performing?"
      action={<ExportButtons onExportCsv={onExportCsv} onExportPdf={onExportPdf} loading={loading} busy={exporting} />}
    >
      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile label="Sent to compliance" value={k?.sent} color={CHART.indigo} loading={loading} />
        <KpiTile label="Approved (in range)" value={k?.approved} color={CHART.green} loading={loading} />
        <KpiTile label="In progress" value={k?.inProgress} color={CHART.blue} loading={loading} />
        <KpiTile label="Compliance rate" value={k?.complianceRate} unit="%" color={rateColor} loading={loading} />
        <KpiTile label="Median time to comply" value={fmtDays(k?.medianTimeToComplianceDays)} color={CHART.sky} loading={loading} hint="candidate → approved" />
        <KpiTile label="p90 time to comply" value={fmtDays(k?.p90TimeToComplianceDays)} color={CHART.violet} loading={loading} hint="slowest 10%" />
      </div>

      {/* Trend — the single time-series on the page */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <p className="text-[12px] font-medium text-text-primary">Compliance rate &amp; volume over time</p>
          {!loading && data?.trendBucketedByVolumeFloor ? (
            <span
              className="text-[10px] text-text-secondary"
              title="Daily send volume is low enough that a daily rate would look noisy, so this chart defaults to weekly buckets. Switch to daily manually if you want it."
            >
              (bucketed weekly — daily volume is too low to read as a trend)
            </span>
          ) : null}
        </div>
        {loading ? (
          <Skeleton className="h-56 w-full" />
        ) : trend.length === 0 ? (
          <EmptyNote>No activity in this period yet.</EmptyNote>
        ) : (
          <div className="w-full h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="rateFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.indigo} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART.indigo} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border-primary" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "currentColor" }}
                  className="text-text-secondary"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 10, fill: "currentColor" }} className="text-text-secondary" tickLine={false} axisLine={false} width={44} />
                <Tooltip content={<TrendTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  iconType="plainline"
                  formatter={(v) => <span className="text-text-secondary">{v === "rate" ? "Compliance rate (%)" : "Sent (volume)"}</span>}
                />
                <Area type="monotone" dataKey="rate" name="rate" stroke={CHART.indigo} strokeWidth={2} fill="url(#rateFill)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="sent" name="sent" stroke={CHART.sky} strokeWidth={2} fill="none" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Section>
  );
};

export default OverviewSection;
